/**
 * WordPress dependencies
 */
import { PanelBody, Path, SVG } from '@safe-wordpress/components';
import { useEffect, useMemo, useRef } from '@safe-wordpress/element';
import { useDispatch, useSelect } from '@safe-wordpress/data';
import { _x, sprintf } from '@safe-wordpress/i18n';

/**
 * External dependencies
 */
import { last, truncate } from 'lodash';
import { store as NAB_DATA } from '@nab/data';
import { getElementLabel } from '@nab/utils';
import type { CssWorkingContentValue } from '@nab/types';

/**
 * Internal dependencies
 */
import './style.scss';

import { ElementEditor } from './element-editor';
import { ImageEditor } from './image-editor';

export const ContentEditor = (): JSX.Element | null => {
	const ref = useMaybeScrollToLastEffect();

	const contentValues = useContentValues();
	if ( ! contentValues.length ) {
		return (
			<div className="nab-css-editor-sidebar__content-editor nab-css-editor-sidebar__content-editor--empty">
				<SelectionIcon />
				<p>
					{ _x(
						'Select an element on the page to start making changes.',
						'user',
						'nelio-ab-testing'
					) }
				</p>
			</div>
		);
	}

	return (
		<div ref={ ref } className="nab-css-editor-sidebar__content-editor">
			{ contentValues.map( ( cv ) => (
				<ContentValueView key={ cv.selector } value={ cv } />
			) ) }
		</div>
	);
};

// ============
// HELPER VIEWS
// ============

const ContentValueView = ( {
	value,
}: {
	readonly value: CssWorkingContentValue;
} ): JSX.Element => {
	const [ activeSelector, setActiveSelector ] = useActiveSelector();
	const View = useMemo(
		() =>
			( (): ( () => JSX.Element | null ) => {
				switch ( value.type ) {
					case 'image':
						return () => (
							<ImageEditor selector={ value.selector } />
						);
					case 'element':
						return () => (
							<ElementEditor selector={ value.selector } />
						);
				}
			} )(),
		[ value.type, value.selector ]
	);

	return (
		<PanelBody
			opened={ activeSelector === value.selector }
			title={ summarize(
				sprintf(
					'%1$s – %2$s',
					getElementLabel( value.selector ),
					getContent( value )
				)
			) }
			onToggle={ () => setActiveSelector( value.selector ) }
		>
			<div className="nab-content-panel__content">
				<View />
			</div>
		</PanelBody>
	);
};

const SelectionIcon = (): JSX.Element => (
	<SVG
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 24 24"
		width="48"
		height="48"
	>
		<Path d="M11.5,11L17.88,16.37L17,16.55L16.36,16.67C15.73,16.8 15.37,17.5 15.65,18.07L15.92,18.65L17.28,21.59L15.86,22.25L14.5,19.32L14.24,18.74C13.97,18.15 13.22,17.97 12.72,18.38L12.21,18.78L11.5,19.35V11M10.76,8.69A0.76,0.76 0 0,0 10,9.45V20.9C10,21.32 10.34,21.66 10.76,21.66C10.95,21.66 11.11,21.6 11.24,21.5L13.15,19.95L14.81,23.57C14.94,23.84 15.21,24 15.5,24C15.61,24 15.72,24 15.83,23.92L18.59,22.64C18.97,22.46 19.15,22 18.95,21.63L17.28,18L19.69,17.55C19.85,17.5 20,17.43 20.12,17.29C20.39,16.97 20.35,16.5 20,16.21L11.26,8.86L11.25,8.87C11.12,8.76 10.95,8.69 10.76,8.69M15,10V8H20V10H15M13.83,4.76L16.66,1.93L18.07,3.34L15.24,6.17L13.83,4.76M10,0H12V5H10V0M3.93,14.66L6.76,11.83L8.17,13.24L5.34,16.07L3.93,14.66M3.93,3.34L5.34,1.93L8.17,4.76L6.76,6.17L3.93,3.34M7,10H2V8H7V10" />
	</SVG>
);

// =======
// HELPERS
// =======

function getContent( cv: CssWorkingContentValue ): string {
	switch ( cv.type ) {
		case 'element':
			return (
				html2text( cv.html ).trim() ||
				html2text( cv.originalHtml ).trim() ||
				_x( 'Empty content', 'text', 'nelio-ab-testing' )
			);

		case 'image':
			return (
				cv.alt.trim() ||
				cv.originalAlt.trim() ||
				last( cv.src.split( '/' ) )?.trim() ||
				last( cv.originalSrc.split( '/' ) )?.trim() ||
				_x( 'Empty alt text', 'text', 'nelio-ab-testing' )
			);
	}
}

const AUX = document.createElement( 'div' );
function html2text( html = '' ) {
	AUX.innerHTML = html.replace( /<br>/gi, ' ' ).replace( /<\/li>/gi, ' ' );
	return ( AUX.textContent || '' ).replace( /\s+/g, ' ' );
}

function summarize( text = '' ): string {
	return truncate( ( text ?? '' ).replace( /\s+/, ' ' ).trim(), {
		length: 35,
		omission: '…',
	} );
}

// =====
// HOOKS
// =====

const useContentValues = () => {
	const editor = useSelect(
		( select ) =>
			select( NAB_DATA ).getPageAttribute( 'css-editor/cssEditorState' ),
		[]
	);

	if ( ! editor || editor.mode !== 'content-editor' ) {
		return [];
	}

	return editor.contentValues;
};

const NOTHING: ReturnType< typeof useActiveSelector > = [ '', () => void null ];
const useActiveSelector = (): [ string, ( selector: string ) => void ] => {
	const editor = useSelect(
		( select ) =>
			select( NAB_DATA ).getPageAttribute( 'css-editor/cssEditorState' ),
		[]
	);
	const { setPageAttribute } = useDispatch( NAB_DATA );

	if ( ! editor || editor.mode !== 'content-editor' ) {
		return NOTHING;
	}

	return [
		editor.activeSelector,
		( activeSelector ) => {
			if ( activeSelector === editor.activeSelector ) {
				return;
			}
			void setPageAttribute( 'css-editor/cssEditorState', {
				...editor,
				activeSelector,
			} );
		},
	];
};

// This effect is use to scroll to the last item in list.
const useMaybeScrollToLastEffect = () => {
	const ref = useRef< HTMLDivElement | null >( null );
	const contentValues = useContentValues();
	const [ activeSelector ] = useActiveSelector();

	const lastSelector = last( contentValues )?.selector;
	useEffect( () => {
		if ( ! ref.current || lastSelector !== activeSelector ) {
			return;
		}
		ref.current.scrollTo( {
			top: ref.current.scrollHeight,
			behavior: 'smooth',
		} );
	}, [ lastSelector, activeSelector ] );

	return ref;
};
